import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Grid,
  Box,
  TextField,
  FormControl,
  MenuItem,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import * as Yup from "yup";
import { UPDATE_ANNOUNCEMENTS, GET_BY_ID_ANNOUNCEMENTS } from "app/redux/actions/announcements";
import { useAuth } from "app/hooks/useAuth";
import { toast } from "react-toastify";
import Loading from "app/components/MatxLoading";

const EditAnnouncement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams(); // ✅ announcement id from route params
  let auth = useAuth();

  const { getById, getBYIdLoading, editLoading } = useSelector(
    (state) => state.announcement
  );

  // ✅ Fetch announcement details when page loads
  useEffect(() => {
    if (id) {
      dispatch(GET_BY_ID_ANNOUNCEMENTS(id, auth.token));
    }
  }, [id, dispatch, auth.token]);

  // ✅ Validation schema
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      title: Yup.string().required("Title is required."),
      message: Yup.string().required("Message is required."),
      type: Yup.string().required("Type is required."),
      targetRole: Yup.string().required("Target role is required."),
      expiresAt: Yup.date().nullable(),
    });
  }, []);

  // ✅ Submit handler
  const handleSubmit = (values) => {
    const payload = {
      id,
      title: values.title,
      message: values.message,
      type: values.type,
      targetRole: values.targetRole,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
    };

    dispatch(UPDATE_ANNOUNCEMENTS(id, payload, auth.token, moveToNext, moveToFailure));
  };

  const moveToNext = () => {
    toast.success("Announcement Updated Successfully");
    navigate(-1);
  };

  const moveToFailure = (message) => {
    toast.error(message);
  };

  if (getBYIdLoading) return <Loading />;

  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Edit Announcement" }]} />
      </Box>
      <Box padding={2}>
        <Formik
          enableReinitialize
          initialValues={{
            title: getById?.title || "",
            message: getById?.message || "",
            type: getById?.type || "General",
            targetRole: getById?.targetRole || "All",
            expiresAt: getById?.expiresAt
              ? new Date(getById.expiresAt)
              : null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
            submitCount,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Title */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Title *"
                    name="title"
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={(touched.title || submitCount > 0) && Boolean(errors.title)}
                    helperText={(touched.title || submitCount > 0) && errors.title}
                  />
                </Grid>

                {/* Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Type *"
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      error={(touched.type || submitCount > 0) && Boolean(errors.type)}
                      helperText={(touched.type || submitCount > 0) && errors.type}
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Holiday">Holiday</MenuItem>
                      <MenuItem value="Urgent">Urgent</MenuItem>
                      <MenuItem value="Event">Event</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>

                {/* Target Role */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Target Role *"
                      name="targetRole"
                      value={values.targetRole}
                      onChange={handleChange}
                      error={(touched.targetRole || submitCount > 0) && Boolean(errors.targetRole)}
                      helperText={(touched.targetRole || submitCount > 0) && errors.targetRole}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="teamlead">Team Lead</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>

                {/* Expiry Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Expires At"
                        value={values.expiresAt}
                        onChange={(date) => setFieldValue("expiresAt", date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: (touched.expiresAt || submitCount > 0) && Boolean(errors.expiresAt),
                            helperText: (touched.expiresAt || submitCount > 0) && errors.expiresAt,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField
                    label="Message *"
                    name="message"
                    fullWidth
                    multiline
                    rows={4}
                    value={values.message}
                    onChange={handleChange}
                    error={(touched.message || submitCount > 0) && Boolean(errors.message)}
                    helperText={(touched.message || submitCount > 0) && errors.message}
                  />
                </Grid>

                {/* Submit */}
                {editLoading === true ? (
                  <Loading />
                ) : (
                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Update Announcement
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </SimpleCard>
  );
};

export default EditAnnouncement;
